import type { LensTheme } from '@diligentcorp/atlas-theme-mui-lens';
import {
  Box,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
  useTheme,
} from '@mui/material';
import React from 'react';
import { useCustomFieldAuditEntriesQuery } from 'src/graphql';
import { textStyleObject, typographySemantic } from 'src/theme/atlas';
import { DateFormats, formatDate } from 'src/utils/time';

interface Props {
  customFieldId: string;
}

const COLUMNS = ['User', 'Event', 'Timestamp'];

export const CustomFieldAuditLog: React.FC<Props> = ({ customFieldId }) => {
  const { tokens } = useTheme<LensTheme>();
  const sp = tokens.semantic.spacing.desktop.gap;

  const { data, isLoading, isError } = useCustomFieldAuditEntriesQuery({
    input: { customFieldId },
  });

  if (isLoading) {
    return (
      <Box
        sx={{ display: 'flex', justifyContent: 'center', p: sp[3].value }}
        data-testid="custom-field-audit-log-loading"
      >
        <CircularProgress />
      </Box>
    );
  }

  if (isError) {
    return (
      <Typography
        sx={{
          p: sp[2].value,
          ...textStyleObject(typographySemantic.bodySmall),
        }}
        data-testid="custom-field-audit-log-error"
      >
        We couldn&apos;t load the audit log for this field. Please try again.
      </Typography>
    );
  }

  const entries = data?.customFieldAuditEntries?.items ?? [];

  return (
    <Box data-testid="custom-field-audit-log">
      <Table aria-label="Custom field audit log" size="small">
        <TableHead>
          <TableRow>
            {COLUMNS.map((col) => (
              <TableCell key={col}>{col}</TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {entries.map((entry) => (
            <TableRow key={entry.id} data-testid="custom-field-audit-log-row">
              <TableCell>{entry.user ?? 'System'}</TableCell>
              <TableCell>{entry.description}</TableCell>
              <TableCell>
                {formatDate(
                  entry.createdAt,
                  DateFormats.YYYY_MM_DD_TIME_SHORT,
                  false,
                )}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Box>
  );
};
